import AppBackButton from '@nxsys/components/AppBackButton';
import AppCard from '@nxsys/components/AppCard';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultPaymentFrequency,
  defaultRegistrationStatus,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useGetCompanyPaymentGrouping } from '@nxsys/hooks/api/dashboard';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { formatStatus } from '@nxsys/utils';
import { Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, filter, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const AttachmentOrderModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const { employeeList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { list, pagination, loading, queryParams } = employeeList;

  const [isGroupAttachmentOrder, setIsGroupAttachmentOrder] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null,
  );

  const {
    value: companyPaymentGrouping,
    loading: isCompanyPaymentGroupingLoading,
    run: getCompanyPaymentGrouping,
  } = useGetCompanyPaymentGrouping();
  const companyPaymentGroupingData = useMemo(
    () => companyPaymentGrouping?.data,
    [companyPaymentGrouping],
  );

  useEffect(() => {
    if (isOpen) {
      getCompanyPaymentGrouping({
        isActiveAttachment: true,
        paymentModel: PAYMENT_MODEL_ENUM.PAYEE,
      });
    }
  }, [isOpen]);

  const handleFetchData = (params: GetEmployeeListParams) => {
    const newParams = {
      ...params,
      haveActiveAttachment: true,
      companyIds: selectedCompanyId ? [selectedCompanyId] : undefined,
      paymentModel: PAYMENT_MODEL_ENUM.PAYEE,
      appName: 'BUREAU',
    };
    dispatch(employeeActions.getEmployeeList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const columns: ColumnsType<EmployeeRecordType> = [
    {
      key: 'actions',
      title: 'Action',
      fixed: 'left',
      align: 'left',
      width: 100,
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li
            onClick={() => navigate(`/employees-subcontractors/${record.id}`)}
          >
            <Icons.IconEye />
          </li>
        </ul>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
      width: 140,
    },
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'title',
      title: 'Title',
      dataIndex: 'title',
      width: 120,
    },
    {
      key: 'name',
      title: 'Name',
      render: (props) => `${props?.forename || ''} ${props?.surname || ''}`.trim(),
      width: 220,
    },
    {
      key: 'emailId',
      title: 'Email ID',
      dataIndex: 'email',
      width: 300,
    },
    {
      key: 'mobileNumber',
      title: 'Mobile Number',
      dataIndex: 'mobile',
    },
    {
      key: 'companyPayment',
      title: 'Employer',
      dataIndex: 'companyPayment',
      width: 300,
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'registrationStatus',
      title: 'Reg Status',
      dataIndex: 'registrationStatus',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultRegistrationStatus, (item) => item.value === value)
          ? filter(defaultRegistrationStatus, (item) => item.value === value)[0]
              ?.label
          : '',
      width: 240,
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultPaymentFrequency, (item) => item?.value === value)
          ? filter(defaultPaymentFrequency, (item) => item?.value === value)[0]
              ?.label
          : '',
    },
    {
      key: 'NINumber',
      title: 'NI Number',
      dataIndex: 'NINumber',
      width: 180,
    },
    {
      key: 'NiCategory',
      title: 'NI Category',
      dataIndex: 'setting',
      render: (value) => value?.niCategory || '_',
    },
    {
      key: 'taxCode',
      title: 'Tax Code',
      dataIndex: 'setting',
      render: (value) => value?.taxCode || '_',
    },
    {
      key: 'lastPaidDate',
      title: 'Last Paid Date',
      dataIndex: 'lastPaidDate',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '_'),
    },
    {
      key: 'lastPaidPeriod',
      title: 'Last Paid Period',
      dataIndex: 'lastPaidPeriod',
    },
  ];

  const handleSelectCompany = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setIsGroupAttachmentOrder(false);
    handleFetchData({
      page: 0,
      size: 20,
    });
  };

  return (
    <AppModalContainer
      title="Attachment Orders"
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
    >
      {isGroupAttachmentOrder ? (
        <AppCard
          loading={isCompanyPaymentGroupingLoading}
          className="!border-none"
        >
          <div className="grid grid-cols-2 gap-[16px]">
            {isEmpty(companyPaymentGroupingData) ? (
              <div className="col-span-2">
                <AppEmpty title="No attachment order found" />
              </div>
            ) : (
              companyPaymentGroupingData?.map((item) => (
                <div
                  key={item.companyPaymentId}
                  className="flex items-center justify-between border border-neutral-100 rounded-[8px] p-[16px] cursor-pointer"
                  onClick={() => handleSelectCompany(item.companyPaymentId)}
                >
                  <span>{item.companyPayment}</span>
                  <Tag>{`${item.countSubcontractor} employees`}</Tag>
                </div>
              ))
            )}
          </div>
        </AppCard>
      ) : (
        <AppsContent>
          <div className="flex items-center justify-between gap-x-[16px] mb-[20px]">
            <AppBackButton onClick={() => setIsGroupAttachmentOrder(true)} />
            <AppInputSearch onChange={handleSearch} />
          </div>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2700 }}
            columns={columns}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
          />
        </AppsContent>
      )}
    </AppModalContainer>
  );
};

export default AttachmentOrderModal;
